import React, { useEffect, useMemo, useState } from 'react';
import { T } from './speedTheme';
import { runesFromAccount, unitsFromAccount, statsWith, setName, StatLine } from './runeEngine';
import { runeStoneSrc, setGlyphSrc } from './RuneStar';
import { TacticBuild } from './TacticBuild';
import { RawAccount } from '../utils/runeStore';
import { buildIdIndex } from '../utils/swexRoster';
import { getMonsterImgPath } from '../utils/monster';
import { mobImgUrl } from '../utils/monsterCells';
import RuneDetail from './RuneDetail';
import { useBackHandler } from '../hooks/useBackHandler';
import BackButton from '../components/BackButton';

/**
 * **룬 고급 설정** — 게임의 [룬 고급 설정] 창을 룬피팅 탭에 옮겼다 (사용자 2026-10-28 "이것처럼 구현해", 게임 화면 세 장).
 *
 *   1) 설정 — 세트(4세트 + 2세트) · 룬 주옵션(2·4·6번) · 목표 능력치(이상/이하, 체크한 것만). 목표 능력치는 **늘 기본 능력치 제외**
 *      — 맨몸을 뺀 룬·세트·아티 몫으로 적고 찾는다 (사용자 2026-10-28 "항상 기본능력치 제외 기준으로" — 예전엔 체크칸)
 *   2) [시작] — 서버(POST /api/sim/rune-search → swSim runesearch.ts → C++ searchBuilds)가 조건을 채우는 룬 여섯 묶음을 여럿 찾는다
 *   3) 결과 — 칸마다 정렬 값(공격속도순이면 공속), 누르면 변경 전 → 후 능력치(▲▼)와 룬 여섯(어느 몹에서 빼 오는지), [변경 적용]
 *
 * 변경 전 = 지금 룬피팅 탭이 이 몹에 끼운 룬. 몹은 게임 자료 맨몸, 아티팩트는 지금 끼운 둘 그대로.
 * 해제 비용(마나)은 게임 규칙을 아직 몰라 셈하지 않고 '다른 몹에서 빼 오는 룬 수'만 보인다.
 */

const STAT_KEYS = ['hp', 'atk', 'def', 'spd', 'cr', 'cd', 'res', 'acc'] as const;
type StatKey = typeof STAT_KEYS[number];
const STAT_LABEL: Record<StatKey, string> = { hp: '체력', atk: '공격력', def: '방어력', spd: '공격속도', cr: '치명 확률', cd: '치명 피해', res: '효과 저항', acc: '효과 적중' };
/** 서버(base_stats) 이름 */
const SERVER_KEY: Record<StatKey, string> = { hp: 'hp', atk: 'atk', def: 'def', spd: 'spd', cr: 'cri_rate', cd: 'cri_dmg', res: 'res', acc: 'acc' };
const FOUR_SETS = [13, 3, 10, 5, 8, 11];
const TWO_SETS = [15, 16, 17, 4, 6, 7, 1, 2, 14, 18, 19, 20, 21, 22, 23, 24];
/** 칸마다 고를 수 있는 주옵 (효과번호) — 2번 공속·체공방%, 4번 체공방%·치확·치피, 6번 체공방%·효저·효적 */
const MAIN_CHOICES: Record<2 | 4 | 6, Array<[number, string]>> = {
    2: [[8, '공격속도'], [2, '체력 %'], [4, '공격력 %'], [6, '방어력 %']],
    4: [[2, '체력 %'], [4, '공격력 %'], [6, '방어력 %'], [9, '치명 확률'], [10, '치명 피해']],
    6: [[2, '체력 %'], [4, '공격력 %'], [6, '방어력 %'], [11, '효과 저항'], [12, '효과 적중']]
};
const SORTS: Array<[StatKey | 'eff', string]> = [
    ['spd', '공격속도순'], ['eff', '룬 점수순'], ['hp', '체력순'], ['atk', '공격력순'], ['def', '방어력순'],
    ['cr', '치명 확률순'], ['cd', '치명 피해순'], ['res', '효과 저항순'], ['acc', '효과 적중순']
];

interface Found { runes: number[]; stats: Record<string, number>; sets: number[]; eff: number }

/**
 * **자동 체크 임계점** (사용자 2026-10-28 "일정 %(임계점)가 넘은 옵션들은 룬 고급설정에 체크하고 그 수치를 넣어줘") —
 * 원본 룬작에서 룬·세트·아티로 올린 몫(기본 능력치 제외)이 '룬으로 끝까지 올릴 수 있는 만큼'의 AUTO_ON(40%)을 넘으면 그 능력치를 켠다.
 *   끝까지 = 공속 100 · 치확 85 · 치피 150 · 효저 100 · 효적 100 (%p) · 체력 맨몸의 300% · 공격·방어 맨몸의 250%
 * 숫자는 그 몫 그대로 '이상'에 넣는다. 룬피팅 목표에서 켠 문턱(preset.on)은 임계점과 상관없이 켠다.
 */
const AUTO_ON = 0.4;
/**
 * 기본 능력치 포함으로 적는 능력치 — 치확·치피·효저·효적은 몹 창에 보이는 합계 그대로 (사용자 2026-10-28 "효저, 치확, 치피, 효적은 기본값 포함").
 * 체·공·방·공속만 맨몸을 뺀 몫. 서버는 모두 맨몸 뺀 몫으로 셈하므로(excludeBase) 보낼 때 이 넷은 맨몸을 뺀다.
 */
const WITH_BASE = new Set<StatKey>(['cr', 'cd', 'res', 'acc']);
const FULL_GAIN = (k: StatKey, base: number): number =>
    ({ spd: 100, cr: 85, cd: 150, res: 100, acc: 100, hp: base * 3, atk: base * 2.5, def: base * 2.5 } as Record<StatKey, number>)[k];
interface Row { on: boolean; lo: string; hi: string }

/** 원본 몹 한 마리 → 옵티마이저 셈 재료 */
function unitOf(raw: any) {
    try {
        const account = { unit_list: [{ ...raw, unit_id: 1 }] };
        const unit = unitsFromAccount(account)[0] || null;
        const runes = runesFromAccount(account).runes.sort((a, b) => a.slot - b.slot);
        return unit ? { unit, runes } : null;
    } catch {
        return null;
    }
}
const total = (lines: StatLine[] | null, k: StatKey) => {
    const l = lines?.find((x) => x.key === k);
    return l ? l.base + l.bonus : 0;
};

/**
 * 처음 설정값 — 룬피팅이 맞춘 목표(세트 · 공속·치확·효적·효저 문턱 · 체·공·방·치피 원본 숫자)를 그대로 불러와 사람이 고친다
 * (사용자 2026-10-28 "목표 칩 줄은 빼버리고 룬 고급설정을 불러온거로 해서 조정가능하게"). on 인 능력치만 '이상'으로 켠다.
 */
export interface AdvancedPreset { four: number | null; two: number[]; lo: Partial<Record<StatKey, number>>; on: StatKey[]; /** 켜지 않을 능력치 — 풍드나 덱의 공격속도 */ off?: StatKey[] }
export type { StatKey };

export default function RuneAdvanced({ unitMasterId, current, source, preset, account, monsterMapping, isMobile, onApply, onActive }: {
    unitMasterId: number;
    /** 변경 전 — 지금 끼운 몹 원본(맨몸·룬·아티) */
    current: any;
    /** 원본 룬작 — 처음 설정값(세트·주옵·공속) */
    source: TacticBuild | null;
    /** 룬피팅 목표 — 있으면 세트·능력치 칸을 이것으로 (원본 룬작 source 보다 앞) */
    preset?: AdvancedPreset | null;
    account: RawAccount | null;
    monsterMapping: any;
    isMobile: boolean;
    onApply: (raw: any) => void;
    /** 결과가 떠 있는 동안 참 — 룬피팅 탭이 위 몹 능력치·룬 판을 감춘다 (사용자 2026-10-28 "룬 고급설정 중에는 이거 표시 하지말아보자") */
    onActive?: (active: boolean) => void;
}) {
    // ── 설정 (처음은 원본 룬작에서) ──
    const [four, setFour] = useState<number>(() => (preset ? preset.four || 0 : (source?.sets || []).find((x) => FOUR_SETS.includes(x)) || 0));
    const [two, setTwo] = useState<number[]>(() => (preset ? preset.two.slice(0, 3) : (source?.sets || []).filter((x) => !FOUR_SETS.includes(x)).slice(0, 3)));
    const before = useMemo(() => unitOf(current), [current]);
    /** 맨몸 능력치 (게임 자료 6성 40) — 기본 능력치 제외 셈에 */
    const baseOf = useMemo<Record<StatKey, number>>(() => {
        const b = before?.unit.base;
        return b
            ? { hp: b.hp, atk: b.atk, def: b.def, spd: b.spd, cr: b.critRate, cd: b.critDmg, res: b.resist, acc: b.accuracy }
            : { hp: 0, atk: 0, def: 0, spd: 0, cr: 0, cd: 0, res: 0, acc: 0 };
    }, [before]);
    // 아티팩트·유물 주옵까지 (유물은 몹 원본의 것 — 룬피팅이 고른 유물)
    const beforeLines = useMemo(() => (before ? statsWith(before.unit, before.runes, before.unit.artifacts) : null), [before]);
    /**
     * 2·4·6번 주옵 — **필요할 때만** 고정한다(나머지는 전체). 원본 룬작의 주옵을 그대로 옮기면 쓸데없이 좁혀 결과가 줄었다 (사용자 2026-10-28
     * "세트는 지키더라도 룬 주옵션은 필요할때만 — 필요 공속이 일정치 이상 높아서 2번 공속을 껴야하는 경우").
     *   2번 공속   룬으로 올릴 공속(목표 − 맨몸 − 신속 세트)이 70 넘으면 — 부옵만으로는 거기까지 어렵다
     *   4번 치피   치피 합계 150 넘으면
     *   치확(4번)·효저·효적(6번)은 고정하지 않는다 (사용자 2026-10-28 "효저 효적 아님. 치확 아님. 치피는 맞음. 공속 맞음")
     */
    const [mains, setMains] = useState<Record<2 | 4 | 6, number>>(() => {
        const want = (k: StatKey) => Number(preset?.lo[k] ?? (source as any)?.[k] ?? total(beforeLines, k)) || 0;
        const fourSet = preset ? preset.four : (source?.sets || []).find((x) => FOUR_SETS.includes(x)) || 0;
        const spdNeed = want('spd') - baseOf.spd - (fourSet === 3 ? Math.ceil(baseOf.spd * 0.25) : 0);
        return { 2: spdNeed > 70 && !preset?.off?.includes('spd') ? 8 : 0, 4: want('cd') > 150 ? 10 : 0, 6: 0 };
    });
    const [rows, setRows] = useState<Record<StatKey, Row>>(() => {
        const o = {} as Record<StatKey, Row>;
        const src: Record<StatKey, number | undefined> = source
            ? { hp: source.hp, atk: source.atk, def: source.def, spd: source.spd, cr: source.cr, cd: source.cd, res: source.res, acc: source.acc }
            : { hp: undefined, atk: undefined, def: undefined, spd: undefined, cr: undefined, cd: undefined, res: undefined, acc: undefined };
        // 처음 숫자 — 체·공·방·공속은 최종 능력치에서 맨몸을 뺀 몫, 치확·치피·효저·효적은 합계 그대로(WITH_BASE)
        for (const k of STAT_KEYS) {
            const full = preset?.lo[k] ?? src[k] ?? total(beforeLines, k);
            const v = full ? Math.max(0, full - baseOf[k]) : 0;
            const shown = WITH_BASE.has(k) ? full || 0 : v;
            // 임계점(룬으로 올린 몫 기준)을 넘은 능력치 + 룬피팅 목표에서 켠 문턱을 켠다. 나머지는 숫자만 채워 두고 사람이 켠다
            const big = v > 0 && FULL_GAIN(k, baseOf[k]) > 0 && v >= AUTO_ON * FULL_GAIN(k, baseOf[k]);
            o[k] = { on: v > 0 && !preset?.off?.includes(k) && (big || !!preset?.on.includes(k) || (!preset && k === 'spd')), lo: shown ? String(Math.round(shown)) : '', hi: '' };
        }
        return o;
    });
    const excludeBase = true;

    // ── 결과 ──
    const [found, setFound] = useState<Found[] | null>(null);
    useEffect(() => () => onActive?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [sort, setSort] = useState<StatKey | 'eff'>('spd');
    /** 그다음 정렬 — 1차 값이 같을 때 (게임의 두 번째 정렬 칸) */
    const [sort2, setSort2] = useState<StatKey | 'eff'>('eff');
    /**
     * 보는 화면 — 조건(설정) ↔ 결과. [시작]으로 찾으면 결과 화면으로 바뀌고, 뒤로 가기(‹ 단추 · 브라우저 뒤로)면 조건으로 돌아온다
     * (사용자 2026-10-29 "시작하면 이 화면으로 전환되고 여기서 뒤로가면 룬 고급설정으로"). 결과 화면 동안은 위 몹 능력치·룬 판을 감춘다
     */
    const [view, setView] = useState<'settings' | 'results'>('settings');
    const showResults = !!found && found.length > 0 && view === 'results';
    const noCombo = !!found && found.length === 0;
    useEffect(() => { onActive?.(showResults); }, [showResults]); // eslint-disable-line react-hooks/exhaustive-deps
    useBackHandler(showResults, () => setView('settings'));
    const [picked, setPicked] = useState<number | null>(null);
    /** 누른 룬 — 위 능력치 칸 자리에 룬 창을 보인다. 같은 룬을 다시 누르거나 × 면 능력치로 (사용자 2026-10-28 "룬을 선택하면 위에 표시 · 선택해제하면 지금처럼") */
    const [runePop, setRunePop] = useState<any | null>(null);
    const runePopParsed = useMemo(() => {
        if (!runePop) return null;
        try { return runesFromAccount({ unit_list: [], runes: [runePop] }).runes[0] || null; } catch { return null; }
    }, [runePop]);

    /** 계정 룬 — 번호로 찾기 · 어느 몹이 끼고 있나 */
    const runeIndex = useMemo(() => {
        const byId = new Map<number, any>();
        const owner = new Map<number, number>();
        if (account) {
            for (const r of account.runes) byId.set(Number(r.rune_id), r);
            for (const u of account.units) for (const r of u.runes || []) { byId.set(Number(r.rune_id), r); owner.set(Number(r.rune_id), Number(u.unit_master_id)); }
        }
        return { byId, owner };
    }, [account]);
    const idIndex = useMemo(() => (monsterMapping ? buildIdIndex(monsterMapping) : null), [monsterMapping]);
    const mobImg = (id: number) => {
        const k = idIndex?.get(id);
        const p = k ? getMonsterImgPath(monsterMapping, { name: k.family, attribute: k.attr }).imgPath : null;
        return p ? mobImgUrl(p) : null;
    };

    const start = async () => {
        if (!account) { setErr('룬 옵티마이저 탭에서 계정 JSON 을 먼저 불러와 주세요'); return; }
        const lo: Record<string, number> = {}, hi: Record<string, number> = {};
        for (const k of STAT_KEYS) {
            const r = rows[k];
            if (!r.on) continue;
            // 서버는 맨몸 뺀 몫으로 견준다 — 기본 포함으로 적은 넷은 맨몸을 빼서 보낸다
            const minus = WITH_BASE.has(k) ? baseOf[k] : 0;
            if (r.lo.trim() !== '' && Number.isFinite(Number(r.lo))) lo[SERVER_KEY[k]] = Number(r.lo) - minus;
            if (r.hi.trim() !== '' && Number.isFinite(Number(r.hi))) hi[SERVER_KEY[k]] = Number(r.hi) - minus;
        }
        const m: Record<string, number[]> = {};
        for (const s of [2, 4, 6] as const) if (mains[s]) m[String(s)] = [mains[s]];
        setBusy(true); setErr(''); setPicked(null);
        try {
            const r = await fetch('/api/sim/rune-search', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({
                    // 아티팩트 주옵 + 유물 주옵(서버가 맨몸 기준 깡 수치로)까지 넣은 능력치로 찾는다 (사용자 2026-10-28)
                    unit: unitMasterId, artifacts: current?.artifacts || [], relics: current?.relics || [],
                    spec: { four: four || null, two: two.filter(Boolean), mains: m, lo, hi, excludeBase },
                    account: { runes: [...account.runes, ...account.units.flatMap((u) => u.runes || [])] }
                })
            });
            const d = await r.json();
            if (!d?.success) throw new Error(d?.error || '룬을 찾지 못했습니다');
            setFound(d.builds || []);
            // 조합이 없으면 조건 화면에 그대로 — 안내 줄 자리에 빨간 글씨로 (사용자 2026-10-29)
            if ((d.builds || []).length) setView('results');
        } catch (e: any) {
            setErr(e.message || '룬을 찾지 못했습니다');
        }
        setBusy(false);
    };

    const sorted = useMemo(() => {
        if (!found) return [];
        const valOf = (k: StatKey | 'eff') => (f: Found) => (k === 'eff' ? f.eff : Number(f.stats[SERVER_KEY[k]]) || 0);
        const v1 = valOf(sort), v2 = valOf(sort2);
        return [...found].sort((a, b) => v1(b) - v1(a) || v2(b) - v2(a) || b.eff - a.eff);
    }, [found, sort, sort2]);
    const pickedFound = picked !== null ? sorted[picked] : null;
    /** 고른 결과를 몹 원본으로 — 맨몸·아티는 변경 전 그대로, 룬만 바꾼다 */
    const afterRaw = useMemo(() => {
        if (!pickedFound || !current) return null;
        const runes = pickedFound.runes.map((id) => runeIndex.byId.get(Number(id))).filter(Boolean);
        return runes.length === 6 ? { ...current, runes } : null;
    }, [pickedFound, current, runeIndex]);
    const after = useMemo(() => (afterRaw ? unitOf(afterRaw) : null), [afterRaw]);
    const afterLines = useMemo(() => (after ? statsWith(after.unit, after.runes, after.unit.artifacts) : null), [after]);

    const fs = isMobile ? 11.5 : 13;
    const box: React.CSSProperties = { borderRadius: 14, border: `1px solid ${T.line}`, background: T.sunken, padding: isMobile ? '10px 8px' : '12px 14px' };
    const sel: React.CSSProperties = { padding: '5px 6px', borderRadius: 8, border: `1px solid ${T.line}`, fontSize: fs, fontWeight: 700, background: '#fff', color: '#333' };
    const num: React.CSSProperties = { width: isMobile ? 64 : 80, padding: '4px 6px', borderRadius: 8, border: `1px solid ${T.line}`, fontSize: fs, fontWeight: 700, textAlign: 'right', background: '#fff', color: '#333' };
    const label: React.CSSProperties = { fontSize: fs, fontWeight: 800, color: '#555', minWidth: isMobile ? 64 : 84 };
    const head: React.CSSProperties = { textAlign: 'center', fontSize: isMobile ? 12.5 : 14, fontWeight: 900, color: T.blue, margin: '2px 0 8px' };
    /** 게임 몹 창 차림(짙은 갈색) — 능력치·변경 전후 칸 */
    const gameBox: React.CSSProperties = { ...box, background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24', color: '#f3e6d0' };

    /** 작은 룬 칸 — 룬돌·문양·강화 수치, 다른 몹에서 오면 그 몹 얼굴, 변경 전과 같은 룬이면 E */
    const RuneCell = ({ r, same }: { r: any; same: boolean }) => {
        if (!r) return <div style={{ aspectRatio: '1', borderRadius: 8, background: 'rgba(0,0,0,0.25)' }} />;
        const glyph = setGlyphSrc(Number(r.set_id));
        const own = runeIndex.owner.get(Number(r.rune_id));
        const face = !same && own ? mobImg(own) : null;
        return (
            <div title={`${setName(Number(r.set_id))} ${r.slot_no}번 +${r.upgrade_curr ?? 0} — 누르면 룬 보기`} onClick={() => setRunePop((cur: any) => (cur && Number(cur.rune_id) === Number(r.rune_id) ? null : r))}
                className={runePop && Number(runePop.rune_id) === Number(r.rune_id) ? 'rune-picked' : undefined} style={{
                position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                // 테두리는 누른 룬만 — 낀 룬·바뀌는 룬을 테두리로 가르지 않는다(E·몹 얼굴로 보인다). 누른 룬은 고른 몹 칸과 같은 금테 숨쉬기(App.css .rune-picked)
                background: 'linear-gradient(180deg, #4a3220 0%, #2a1c12 100%)', border: '1px solid #5a3d24'
            }}>
                <img src={runeStoneSrc(Number(r.slot_no))} alt="" style={{ position: 'absolute', inset: '8%', width: '84%', height: '84%', objectFit: 'contain' }} />
                {glyph && <img src={glyph} alt="" style={{ position: 'absolute', left: '30%', top: '26%', width: '40%', height: '40%', objectFit: 'contain' }} />}
                <span style={{ position: 'absolute', left: 3, bottom: 1, fontSize: isMobile ? 9 : 10.5, fontWeight: 900, color: '#fff', textShadow: '0 1px 2px #000' }}>+{r.upgrade_curr ?? 0}</span>
                {same && <span style={{ position: 'absolute', right: 2, bottom: 1, fontSize: isMobile ? 9 : 10.5, fontWeight: 900, color: '#ff8a50', textShadow: '0 1px 2px #000' }}>E</span>}
                {face && <img src={face} alt="" style={{ position: 'absolute', right: 1, bottom: 1, width: '42%', height: '42%', borderRadius: 4, border: '1px solid #000' }} />}
            </div>
        );
    };
    const beforeIds = new Set((current?.runes || []).map((r: any) => Number(r.rune_id)));
    // 칸 차례는 게임 [룬 고급 설정]과 같게 — 룬 판 모양대로 위 줄 6·1·2, 아래 줄 5·4·3 (사용자 2026-10-28 "순서를 이것과 같게")
    const bySlot = (raw: any) => [6, 1, 2, 5, 4, 3].map((s) => (raw?.runes || []).find((r: any) => Number(r.slot_no) === s) || null);
    const moved = afterRaw ? afterRaw.runes.filter((r: any) => !beforeIds.has(Number(r.rune_id)) && runeIndex.owner.has(Number(r.rune_id))).length : 0;

    return (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!showResults && <div style={box}>
                <div style={{ ...head, marginBottom: 8 }}>
                    룬 고급 설정
                    {/* 지난 결과로 — 조건을 안 바꾸고 다시 보고 싶을 때 */}
                    {found && found.length > 0 && <span role="button" onClick={() => setView('results')} style={{ marginLeft: 8, fontSize: fs - 1, color: T.blue, fontWeight: 800, cursor: 'pointer' }}>결과 보기 ›</span>}
                </div>
                {<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* 세트 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={label}>세트</span>
                        <select value={four} onChange={(e) => { const v = Number(e.target.value); setFour(v); if (v && two.length > 1) setTwo(two.slice(0, 1)); }} style={sel}>
                            <option value={0}>전체</option>
                            {FOUR_SETS.map((id) => <option key={id} value={id}>{setName(id)}</option>)}
                        </select>
                        {Array.from({ length: four ? 1 : 3 }).map((_, i) => (
                            <select key={i} value={two[i] || 0} onChange={(e) => { const v = Number(e.target.value); const n = [...two]; n[i] = v; setTwo(n.filter((x, j) => x || j < i)); }} style={sel}>
                                <option value={0}>전체</option>
                                {TWO_SETS.map((id) => <option key={id} value={id}>{setName(id)}</option>)}
                            </select>
                        ))}
                    </div>
                    {/* 룬 주옵션 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={label}>룬 주옵션</span>
                        {([2, 4, 6] as const).map((s) => (
                            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: fs, fontWeight: 800, color: '#555' }}>
                                {s}번
                                <select value={mains[s]} onChange={(e) => setMains({ ...mains, [s]: Number(e.target.value) })} style={sel}>
                                    <option value={0}>전체</option>
                                    {MAIN_CHOICES[s].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                                </select>
                            </label>
                        ))}
                    </div>
                    {/* 목표 능력치 */}
                    <div style={{ fontSize: fs, fontWeight: 900, color: '#555', marginTop: 4 }}>목표 능력치</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '6px 18px' }}>
                        {STAT_KEYS.map((k) => {
                            const r = rows[k];
                            const set = (p: Partial<Row>) => setRows({ ...rows, [k]: { ...r, ...p } });
                            return (
                                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: r.on ? 1 : 0.55 }}>
                                    {/* 이름 칸 너비를 고정 — 숫자 칸이 줄마다 같은 자리에 오게 (사용자 2026-10-28 "숫자박스의 x위치를 일정하게") */}
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, ...label, width: isMobile ? 84 : 96, minWidth: 0, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={r.on} onChange={(e) => set({ on: e.target.checked })} />
                                        {STAT_LABEL[k]}
                                    </label>
                                    <input type="number" inputMode="numeric" className="no-spin" value={r.lo} onChange={(e) => set({ lo: e.target.value, on: true })} style={num} />
                                    <span style={{ fontSize: fs - 1, color: T.sub, fontWeight: 700 }}>이상</span>
                                    <input type="number" inputMode="numeric" className="no-spin" value={r.hi} onChange={(e) => set({ hi: e.target.value, on: true })} style={num} />
                                    <span style={{ fontSize: fs - 1, color: T.sub, fontWeight: 700 }}>이하</span>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                        {noCombo
                            ? <span style={{ fontSize: fs + 2, color: T.red, fontWeight: 900 }}>조건을 채우는 룬 조합이 없습니다 — 조건을 낮춰 보세요</span>
                            : <span style={{ fontSize: fs - 1, color: T.sub, fontWeight: 700 }}>체력·공격력·방어력·공격속도는 기본 능력치 제외(룬·세트·아티 몫) · 치명·효과는 기본 포함 합계</span>}
                        <button type="button" disabled={busy} onClick={start} style={{
                            marginLeft: 'auto', border: 'none', borderRadius: 10, padding: isMobile ? '8px 22px' : '9px 30px', fontSize: isMobile ? 13 : 14, fontWeight: 900,
                            color: '#fff', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1
                        }}>{busy ? '찾는 중…' : '시작'}</button>
                    </div>
                    {err && <div style={{ color: T.red, fontSize: fs, fontWeight: 700 }}>{err}</div>}
                </div>}
            </div>}

            {/*
              게임 [룬 고급 설정 결과] 차림 (사용자 2026-10-28 "이런 식으로 보이게" — 게임 화면) — PC 는 두 칸:
              왼쪽 능력치(기본 + 오른 만큼 + 변경 전 대비 ▲▼) · 변경 전 → 후 룬 · [변경 적용], 오른쪽 결과 격자(정렬 두 개).
              휴대폰은 능력치 → 결과 격자 → 변경 전/후 · [변경 적용] 차례로 쌓는다(grid-template-areas)
            */}
            {showResults && (
                <div style={{
                    display: 'grid', gap: 10,
                    gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)',
                    gridTemplateAreas: isMobile ? '"stats" "res" "runes"' : '"stats res" "runes res"',
                    alignItems: 'start'
                }}>
                    {/* 능력치 — 고른 결과(없으면 변경 전) */}
                    <div style={{ ...gameBox, gridArea: 'stats' }}>
                        {runePopParsed ? (
                            <>
                                <RuneDetail rune={runePopParsed} asIs isMobile={isMobile} bare onClose={() => setRunePop(null)} />
                                {(() => {
                                    const own = runeIndex.owner.get(Number(runePop.rune_id));
                                    const img = own ? mobImg(own) : null;
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: isMobile ? 11.5 : 12.5, fontWeight: 700, color: '#e8d7b8' }}>
                                            {img && <img src={img} alt="" style={{ width: 22, height: 22, borderRadius: 5 }} />}
                                            {own ? (beforeIds.has(Number(runePop.rune_id)) ? '지금 이 몹이 낀 룬' : '다른 몹이 낀 룬 — 적용하면 빼 온다') : '룬 보관함'}
                                        </div>
                                    );
                                })()}
                            </>
                        ) : (<>
                        {pickedFound && (
                            <div style={{ fontSize: isMobile ? 11.5 : 13, fontWeight: 800, color: '#caa052', marginBottom: 6 }}>변경 후 능력치 · 룬 점수 {pickedFound.eff.toFixed(1)}</div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', justifyContent: 'start', textAlign: 'left', gap: isMobile ? '3px 8px' : '4px 12px', fontSize: isMobile ? 12.5 : 14, fontWeight: 700, alignItems: 'baseline' }}>
                            {STAT_KEYS.map((k) => {
                                const lines = afterLines || beforeLines;
                                const l = lines?.find((x) => x.key === k);
                                if (!l) return null;
                                const d = afterLines ? total(afterLines, k) - total(beforeLines, k) : 0;
                                const pct = l.pct ? '%' : '';
                                return (
                                    <React.Fragment key={k}>
                                        <span style={{ color: '#e8d7b8' }}>{STAT_LABEL[k]}</span>
                                        <span style={{ textAlign: 'right' }}>{l.pct ? `${l.base + l.bonus}${pct}` : l.base.toLocaleString()}</span>
                                        <span style={{ color: '#9be26f' }}>{l.pct ? '' : `+${l.bonus.toLocaleString()}`}</span>
                                        <span style={{ color: d > 0 ? '#5fd3ff' : d < 0 ? '#ff6b5a' : '#9a8a70' }}>{!afterLines ? '' : d > 0 ? `▲${d.toLocaleString()}` : d < 0 ? `▼${(-d).toLocaleString()}` : '—'}</span>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        </>)}
                    </div>

                    {/* 결과 격자 — 정렬 두 개(1차 · 그다음) */}
                    <div style={{ ...box, gridArea: 'res' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                            {/* 뒤로 — 조건으로 (브라우저 뒤로 가기도 같다). 사이트의 뒤로 가기 동그라미 (사용자 2026-10-29) */}
                            <BackButton onClick={() => setView('settings')} label="조건으로" size={isMobile ? 28 : 30} />
                            <span style={{ fontSize: isMobile ? 12.5 : 14, fontWeight: 900, color: '#444' }}>결과 {found!.length}개</span>
                            <select value={sort} onChange={(e) => { setSort(e.target.value as any); setPicked(null); }} style={{ ...sel, marginLeft: 'auto' }}>
                                {SORTS.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
                            </select>
                            <select value={sort2} onChange={(e) => { setSort2(e.target.value as any); setPicked(null); }} style={sel} title="1차 값이 같을 때">
                                {SORTS.filter(([k]) => k !== sort).map(([k, name]) => <option key={k} value={k}>그다음 {name}</option>)}
                            </select>
                        </div>
                        {!found.length ? (
                            <div style={{ textAlign: 'center', color: T.sub, fontSize: fs, fontWeight: 700, padding: '18px 0' }}>조건을 채우는 룬 조합이 없습니다 — 조건을 낮춰 보세요</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 52 : 58}px, 1fr))`, gap: 6, maxHeight: isMobile ? 220 : 420, overflowY: 'auto', padding: 2 }}>
                                {sorted.map((f, i) => {
                                    const v = sort === 'eff' ? f.eff.toFixed(1) : Math.round(Number(f.stats[SERVER_KEY[sort]]) || 0).toLocaleString();
                                    const on = picked === i;
                                    return (
                                        // 결과 칸 — 금색 단추 대신 사이트 차림의 옅은 칸, 고른 것만 보라로 채운다 (사용자 2026-10-28 "금색버튼대신 깔끔하게")
                                        <button key={f.runes.join('-')} type="button" onClick={() => { setPicked(i); setRunePop(null); }}
                                            onMouseEnter={(e) => { if (!on) e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.6)'; }}
                                            onMouseLeave={(e) => { if (!on) e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.22)'; }}
                                            style={{
                                                padding: '7px 2px', borderRadius: 9, cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 800,
                                                fontVariantNumeric: 'tabular-nums', transition: 'border-color 0.15s ease',
                                                border: on ? '1px solid transparent' : '1px solid rgba(102, 126, 234, 0.22)',
                                                color: on ? '#fff' : '#4b5fd6',
                                                background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(102, 126, 234, 0.06)',
                                                boxShadow: on ? '0 3px 10px rgba(102, 126, 234, 0.35)' : 'none'
                                            }}>{v}</button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* 변경 전 → 변경 후 · [변경 적용] */}
                    <div style={{ ...gameBox, gridArea: 'runes' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center' }}>
                            {[current, afterRaw].map((raw, side) => (
                                <React.Fragment key={side}>
                                    {side === 1 && <span style={{ fontSize: 22, color: '#caa052', fontWeight: 900 }}>›</span>}
                                    <div>
                                        <div style={{ fontSize: isMobile ? 11.5 : 13, fontWeight: 800, color: '#e8d7b8', marginBottom: 5 }}>
                                            {side ? '변경 후' : '변경 전'}
                                            <span style={{ marginLeft: 6, color: '#caa052' }}>{side && pickedFound ? pickedFound.sets.map(setName).join('+') : ''}</span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                                            {bySlot(raw).map((r, i) => <RuneCell key={i} r={r} same={!!r && beforeIds.has(Number(r.rune_id))} />)}
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                        {afterRaw && (
                            <div style={{ textAlign: 'center', marginTop: 10, fontSize: isMobile ? 11.5 : 13, fontWeight: 700, color: '#e8d7b8' }}>
                                다른 몹에서 빼 오는 룬 <b style={{ color: '#ffd98a' }}>{moved}개</b>
                            </div>
                        )}
                        <button type="button" disabled={!afterRaw} onClick={() => { if (afterRaw) { onApply(afterRaw); setPicked(null); setRunePop(null); setFound(null); } }} style={{
                            // [시작]과 같은 사이트 보라 단추 — 밝은 금색 그라데이션은 다크모드 밝기 뒤집기로 칙칙한 띠가 됐다 (사용자 2026-10-28)
                            display: 'block', width: '100%', marginTop: 8, border: 'none', borderRadius: 10, padding: isMobile ? '9px 0' : '10px 0', fontSize: isMobile ? 13 : 14.5, fontWeight: 900,
                            color: '#fff', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: afterRaw ? '0 4px 12px rgba(102, 126, 234, 0.35)' : 'none',
                            cursor: afterRaw ? 'pointer' : 'not-allowed', opacity: afterRaw ? 1 : 0.4
                        }}>변경 적용</button>
                    </div>
                </div>
            )}

        </div>
    );
}
